import moment from "moment";
import MultiSelectComponent from "../../../components/multi-select/multi-select.component";
import { formats } from "../../../config/constants";
import IMEIBasedSuccessRateModel from "../../../models/reports/imei-based-success-rate.model";
import ViewBase from "../../view-base.view";
import IMEIBasedSuccessRateTableView from "./imei-based-success-rate-table.view";

export default function IMEIBasedSuccessRateView() {
  ViewBase.call(this);

  let customers = [];
  let selectedCustomers = [];

  let fromWeek = moment().format(formats.WEEK_FORMAT);
  let toWeek = moment().format(formats.WEEK_FORMAT);

  let isDownloadingSummaryReport = false;
  let isDownloadingRawData = false;

  let searchResult = null;

  let prevSearchCache = {
    searchResult,
    fromWeek,
    toWeek,
    selectedCustomers,
    updateCache() {
      this.searchResult = searchResult;
      this.fromWeek = fromWeek;
      this.toWeek = toWeek;
      this.selectedCustomers = selectedCustomers;
    }
  }

  const STATUS = {
    FAIL: 0,
    SUCCESS: 1,
    TOTAL: 2,
  };

  const handleSelectCustomers = (customers = []) => {
    selectedCustomers = customers;
  };

  const handleChangeFromWeek = (e) => {
    fromWeek = e.target.value;
    if (moment(fromWeek).isAfter(moment(toWeek))) {
      toWeek = fromWeek;
    }
  };

  const handleChangeToWeek = (e) => {
    toWeek = e.target.value;
  };

  const search = () => {
    this.showLoading();

    const criteria = {
      ediClientCode: selectedCustomers.map((customer) => customer.code),
      fromWeek: moment(fromWeek, formats.API_WEEK_FORMAT).format(
        formats.API_WEEK_FORMAT
      ),
      toWeek: moment(toWeek, formats.API_WEEK_FORMAT).format(
        formats.API_WEEK_FORMAT
      ),
    };

    IMEIBasedSuccessRateModel.search(criteria)
      .then((res) => {
        searchResult = res || [];
        prevSearchCache.updateCache();
      })
      .catch((err) => {
        this.handleError(err);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const handleClickClear = () => {
    selectedCustomers = [];
    fromWeek = moment().format(formats.WEEK_FORMAT);
    toWeek = moment().format(formats.WEEK_FORMAT);
  };

  const handleClickDownloadSummaryReport = (e) => {
    e.preventDefault();
    isDownloadingSummaryReport = true;

    const config = {
      ediClientCode: prevSearchCache.selectedCustomers.map((customer) => customer.code),
      fromWeek: moment(prevSearchCache.fromWeek, formats.API_WEEK_FORMAT).format(
        formats.API_WEEK_FORMAT
      ),
      toWeek: moment(prevSearchCache.toWeek, formats.API_WEEK_FORMAT).format(
        formats.API_WEEK_FORMAT
      ),
      filename: "imei_success_rate_summary",
    };

    IMEIBasedSuccessRateModel.exportSummaryReportToExcel(config)
      .catch((err) => {
        this.handleError(err);
      })
      .finally(() => {
        isDownloadingSummaryReport = false;
      });
    return false;
  };

  const handleClickDownloadRawData = (e) => {
    e.preventDefault();
    isDownloadingRawData = true;

    let filename = "imei_success_rate_raw_data";
    let salesDate = "";

    const config = {
      ediClientCode: prevSearchCache.selectedCustomers.map((customer) => customer.code),
      fromWeek: moment(prevSearchCache.fromWeek, formats.API_WEEK_FORMAT).format(
        formats.API_WEEK_FORMAT
      ),
      toWeek: moment(prevSearchCache.toWeek, formats.API_WEEK_FORMAT).format(
        formats.API_WEEK_FORMAT
      ),
      status: STATUS.TOTAL,
      salesDate,
      filename,
    };

    IMEIBasedSuccessRateModel.exportPartialDetailReportToExcel(config)
      .catch((err) => {
        this.handleError(err);
      })
      .finally(() => {
        isDownloadingRawData = false;
      });

    return false;
  };

  const handleClickDownloadRawDataFromCell = (
    dayData,
    status = STATUS.TOTAL,
    client,
  ) => {
    this.showLoading();

    let filenameSuffix = status === STATUS.FAIL
      ? "fail"
      : status === STATUS.SUCCESS
        ? "success"
        : "total"

    let filename = `imei_success_rate_raw_${dayData.clientCode}_${dayData.baseYW}_${dayData.baseYMD}_${filenameSuffix}`;

    let salesDate =
      dayData.baseYMD.toLowerCase() != "total" ? dayData.baseYMD : "";

    const config = {
      ediClientCode: [client.clientCode],
      fromWeek: dayData.baseYW,
      toWeek: dayData.baseYW,
      status,
      salesDate,
      filename,
    };

    IMEIBasedSuccessRateModel.exportPartialDetailReportToExcel(config)
      .catch((err) => {
        this.handleError(err);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  function convertData(data = []) {
    const groupByWeeks = {};
    const groupByClients = {};
    const clientsInfo = {};

    for (let item of data) {
      if (!groupByWeeks[item.baseYW]) {
        groupByWeeks[item.baseYW] = {
          [item.baseYMD]: {
            [item.clientCode]: item,
          },
        };
      } else {
        if (!groupByWeeks[item.baseYW][item.baseYMD]) {
          groupByWeeks[item.baseYW][item.baseYMD] = {
            [item.clientCode]: item,
          };
        } else {
          groupByWeeks[item.baseYW][item.baseYMD][item.clientCode] = item;
        }
      }

      const successRate =
        Number(
          ((Number(item.success) / Number(item.total)) * 100).toFixed(2)
        ) || 0;

      if (!groupByClients[item.clientCode]) {
        groupByClients[item.clientCode] = {
          [item.baseYW]: {
            [item.baseYMD]: {
              ...item,
              successRate,
            },
          },
        };
      } else {
        if (!groupByClients[item.clientCode][item.baseYW]) {
          groupByClients[item.clientCode][item.baseYW] = {
            [item.baseYMD]: {
              ...item,
              successRate,
            },
          };
        } else {
          groupByClients[item.clientCode][item.baseYW][item.baseYMD] = {
            ...item,
            successRate,
          };
        }
      }

      if (!clientsInfo[item.clientCode]) {
        const info = {
          subsCode: item.subsCode,
          clientCode: item.clientCode,
          clientName: item.clientName,
        };
        clientsInfo[item.clientCode] = info;
      }
    }

    return { groupByClients, groupByWeeks, clientsInfo };
  }

  const fetchData = () => {
    this.showLoading();

    IMEIBasedSuccessRateModel.getCustomers()
      .then((res) => {
        customers = res;
      })
      .catch((err) => {
        this.handleError(err);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const handleSubmitSearch = (e) => {
    e.preventDefault();

    if (e.target.checkValidity()) {
      search();
    }

    e.target.classList.add("was-validated");
  };

  return {
    oninit: () => {
      fetchData()
    },

    view: () => {
      return [

        m('.d-flex.justify-content-end.mb-2',
          m('a.btn.btn-primary[data-bs-toggle="collapse"][href="#search-section"][aria-controls="search-section"][aria-expanded="true"]',
            m('i.bi.bi-filter'),
            this.getText("LT00000232", "Filter"),
          ),
        ),

        m('#search-section.collapse.show',
          m(".card",
            m(".card-body",
              m("form.row.g-2.needs-validation[novalidate]", {
                onsubmit: handleSubmitSearch
              },
                m(".col-md-6.col-lg-3.position-relative",
                  m('label.form-label.mandatory[for=customers]', this.getText("LT00008046", 'Customer')),

                  m(MultiSelectComponent, {
                    id: "customers",
                    items: customers,
                    selectedItems: selectedCustomers,
                    filterable: true,
                    itemKey: 'code',
                    itemValue: 'name',
                    required: true,
                    onSelect: handleSelectCustomers,
                  }),

                  m('.invalid-tooltip', this.getText("LT00000729", "This field is required")),
                ),

                m(".col-md-6.col-lg-3.position-relative",
                  m('label.form-label.mandatory[for="week-from"]', this.getText("LT00008047", "From Week")),
                  m('input#week-from.form-control[name="week-from"][type="week"][required]', {
                    value: fromWeek,
                    onchange: handleChangeFromWeek
                  }),
                  m('.invalid-tooltip', this.getText("LT00000729", "This field is required")),
                ),

                m(".col-md-6.col-lg-3.position-relative",
                  m('label.form-label.mandatory[for="week-to"]', this.getText("LT00008048", "To Week")),
                  m('input#week-to.form-control[name="week-to"][type="week"][required]', {
                    value: toWeek,
                    min: fromWeek,
                    onchange: handleChangeToWeek
                  }),
                  m('.invalid-tooltip', this.getText("LT00000729", "This field is required")),
                ),

                m(".row.g-2.justify-content-end.mt-2.mt-md-3",
                  m(".col-12.gap-2.d-flex.justify-content-end.flex-wrap",
                    m("button.btn.btn-primary[type=submit]",
                      this.getText("LT00000001", "Search")
                    ),

                    m("button.btn.btn-white[type=button]",
                      { onclick: handleClickClear },
                      this.getText("LT00000031", "Clear")
                    )
                  )
                )
              ),
            ),
          ),
        ),

        searchResult && m('.card.mt-3',
          m('.card-body',
            searchResult?.length > 0 &&
            m('div',
              m('.d-flex.justify-content-end.mb-2',
                m('.input-group.w-auto',
                  m('button.btn.btn-primary.dropdown-toggle.dropdown-toggle-split[type=button][data-bs-toggle=dropdown][arie-expanded=false]',
                    {
                      disabled: !searchResult,
                    },
                    isDownloadingRawData || isDownloadingSummaryReport
                      ? m('span.spinner-border.spinner-border-sm.me-2[role=status][aria-hidden=true]')
                      : m('i.bi.bi-file-earmark-arrow-down-fill'),
                    this.getText("LT00000492", "Download")
                  ),
                  m('ul.dropdown-menu',
                    m('li',
                      m('a.dropdown-item[href="javascript:void(0);"]', {
                        class: isDownloadingSummaryReport ? 'disabled' : '',
                        onclick: handleClickDownloadSummaryReport
                      },
                        isDownloadingSummaryReport && m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                        this.getText("LT00008049", "Summary Report")
                      )
                    ),
                    m('li',
                      m('a.dropdown-item[href="javascript:void(0);"]', {
                        class: isDownloadingRawData ? 'disabled' : '',
                        onclick: handleClickDownloadRawData
                      },
                        isDownloadingRawData &&
                        m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                        this.getText("LT00008050", "Raw Data")
                      )
                    ),
                  ),
                ),
              ),

              m(IMEIBasedSuccessRateTableView, { tableData: convertData(searchResult), handleClickDownloadRawDataFromCell }),

            ),
            searchResult?.length === 0 && this.getText('LT00000363', 'No items available')
          ),
        ),

        this.getBaseViews(),
      ]
    }
  }
}